import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/styles/BookPage.css';

const TuneUpBook = () => {
  return (
    <div className="book-page">
      {/* Header */}
      <header className="book-header" data-testid="book-header">
        <nav className="book-nav" data-testid="book-nav">
          <Button 
            onClick={() => window.location.href = '/'} 
            variant="ghost" 
            className="back-btn"
            data-testid="back-button"
          >
            <ArrowLeft size={20} /> Back to Home
          </Button>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="book-hero" data-testid="hero-section">
        <div className="book-hero-content">
          <div className="book-cover" data-testid="book-cover">
            <img 
              src="https://static.wixstatic.com/media/7cc6fc_0b0aaabac83049f2922badc76b7f7594~mv2.jpg/v1/fill/w_544,h_409,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/7cc6fc_0b0aaabac83049f2922badc76b7f7594~mv2.jpg" 
              alt="Tune-Up Your Small Business Book Cover"
            />
          </div>
          <div className="book-details">
            <h1 className="book-title" data-testid="book-title">
              Tune-Up Your Small Business
            </h1>
            <p className="book-subtitle" data-testid="book-subtitle">
              Improve Operations, Increase Profitability
            </p>
            <div className="book-meta" data-testid="book-meta">
              <p><strong>Author:</strong> Dr. Raewyn Sleeman (DBA)</p>
              <p><strong>Published:</strong> March 26, 2024</p>
              <p><strong>Pages:</strong> 266</p>
              <p><strong>ISBN:</strong> 9781637425831</p>
            </div>
            <p className="book-intro" data-testid="book-intro">
              Welcome to the ideal guide for small business owners who want to achieve their personal and professional goals by making their businesses more successful.
            </p>
            <a 
              href="https://a.co/d/2fma1s4" 
              target="_blank" 
              rel="noopener noreferrer"
              className="buy-now-btn"
              data-testid="buy-now-main"
            >
              <ExternalLink size={20} /> Buy Now on Amazon
            </a>
          </div>
        </div>
      </section>

      {/* About the Book */}
      <section className="book-about" data-testid="about-section">
        <div className="container">
          <h2 className="section-title" data-testid="about-title">About the Book</h2>
          <div className="about-content">
            <p>
              <strong>Tune-Up Your Small Business</strong> focuses on groundbreaking new research that identifies the regular, small changes that can lead to significant improvements over time for businesses with limited resources.
            </p>
            <p>
              Through stories of small businesses that have grown by following the advice in this book, it offers inspiration and practical advice.
            </p>
            <p>
              Dr. Sleeman's new insights are provided in easy-to-follow steps to help you define what makes your business unique, communicate effectively with your customers, and present your business accurately online.
            </p>
            <p>
              You will find simple advice on how to price your services and reduce expenses without losing value.
            </p>
            <p>
              This book is a step-by-step guide to developing your business steadily and effectively for owners with limited resources (time, people, budget).
            </p>
          </div>
        </div>
      </section>

      {/* Author Bio */}
      <section className="author-bio" data-testid="author-section">
        <div className="container">
          <h2 className="section-title" data-testid="author-title">About the Author</h2>
          <div className="author-content">
            <div className="author-photo">
              <img 
                src="https://static.wixstatic.com/media/7cc6fc_f03bb72f22974b61bf92caabaa64f88a~mv2.png/v1/crop/x_0,y_22,w_637,h_622/fill/w_183,h_178,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Dr%20Raewyn%20Sleeman%2C%20Author%2C%20Tune-up%20Your%20Small%20Business.png" 
                alt="Dr. Raewyn Sleeman"
              />
            </div>
            <div className="author-text">
              <h3>Dr. Raewyn Sleeman (DBA)</h3>
              <p>
                Dr. Raewyn Sleeman is a specialist in aiding small businesses to flourish. With a Doctorate in Business Administration, she has the knowledge to teach business owners about marketing and strategy in ways that are easy to grasp.
              </p>
              <p>
                Having founded the Marketing Strategy App, Dr. Sleeman applies her expertise in a practical setting. She is also committed to teaching and volunteering, reflecting her passion for positive impact.
              </p>
              <p>
                In her book, Dr. Sleeman shares straightforward, actionable advice for you to create a successful business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="reviews" data-testid="reviews-section">
        <div className="container">
          <h2 className="section-title" data-testid="reviews-title">Reviews</h2>
          <div className="review-card">
            <p className="review-text">
              "If you run a small business, read this book! It is full of practical insights and ideas. I especially love the many real-life stories that demonstrate clearly how Dr. Sleeman's ideas work in practice."
            </p>
            <p className="review-author">
              <strong>Jochen Wirtz</strong><br />
              Vice Dean MBA Programmes, National University of Singapore
            </p>
          </div>
        </div>
      </section>

      {/* Where to Buy */}
      <section className="buy-section" data-testid="buy-section">
        <div className="container">
          <h2 className="section-title" data-testid="buy-title">Where to Buy</h2>
          <div className="retailers-grid">
            <a 
              href="https://a.co/d/2fma1s4" 
              target="_blank" 
              rel="noopener noreferrer"
              className="retailer-link"
              data-testid="buy-amazon"
            >
              Amazon
            </a>
            <a 
              href="https://www.businessexpertpress.com/books/tune-up-your-small-business-improve-operations-increase-profitability/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="retailer-link"
              data-testid="buy-bep"
            >
              Business Expert Press
            </a>
            <a 
              href="https://www.barnesandnoble.com/w/tune-up-your-small-business-raewyn-sleeman/1144700213?ean=9781637425831" 
              target="_blank" 
              rel="noopener noreferrer"
              className="retailer-link"
              data-testid="buy-bn"
            >
              Barnes & Noble
            </a>
            <a 
              href="https://www.waterstones.com/book/tune-up-your-small-business/raewyn-sleeman/9781637425831" 
              target="_blank" 
              rel="noopener noreferrer"
              className="retailer-link"
              data-testid="buy-waterstones"
            >
              Waterstones
            </a>
            <a 
              href="https://www.indigo.ca/en-ca/tune-up-your-small-business-improve-operations-increase-profitability/9781637425831.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="retailer-link"
              data-testid="buy-indigo"
            >
              Indigo Chapters
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TuneUpBook;
